import React, { useState, Children } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ClipboardListIcon, TargetIcon, UserIcon, BarChart3Icon, AlertTriangleIcon, PhoneIcon, CheckIcon, ChevronDownIcon, ArrowRightIcon } from 'lucide-react';
import { ScrollingTicker } from '../components/ScrollingTicker';
import { Link } from 'react-router-dom';
interface BuyersProps {
  onBuyerContactOpen: () => void;
}
const fadeInUp = {
  initial: {
    opacity: 0,
    y: 30
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-100px'
  },
  transition: {
    duration: 0.6,
    ease: 'easeOut'
  }
};
const staggerContainer = {
  initial: {},
  whileInView: {
    transition: {
      staggerChildren: 0.15
    }
  },
  viewport: {
    once: true
  }
};
const tickerItems = ['30–45 qualified intros / 90 days', '$5–10M EBITDA focus', '$60K/mo paid in advance', 'Screening-ready summaries + call context', 'Designed to reduce auction exposure'];
const objections = [{
  question: 'We already have an internal sourcing team.',
  answer: "Good — this augments them. Your internal team likely has limited bandwidth for cold outreach to the $5–10M EBITDA segment. We add a dedicated, parallel sourcing channel that generates introductions your internal team can advance. Most clients see us as a force multiplier, not a replacement. The introductions we deliver are opportunities your team wouldn't have found — or wouldn't have found fast enough."
}, {
  question: '$60K/month feels expensive for sourcing.',
  answer: 'Consider the alternative math: a senior deal professional costs $300K–$500K/year fully loaded and generates maybe 2–3 proprietary deals per year. At $60K/month, we deliver 30–45 qualified introductions in 90 days with ~3–5 becoming actionable. On a cost-per-qualified-opportunity basis, this is dramatically more efficient than internal sourcing — and it starts producing in weeks, not quarters. If even one deal closes from the pilot, the ROI is measured in multiples, not percentages.'
}, {
  question: 'How do I know the introductions will be quality?',
  answer: "Two ways: First, ask for our sample deal brief — it shows exactly the level of detail and screening rigor you'll receive with every introduction. Second, the pilot structure itself protects you. Monthly billing means you see results before you pay for the next month. If the quality doesn't meet your standards in Month 1, you have a direct conversation with us — and if we can't fix it, you stop."
}, {
  question: 'What if no deals close from the pilot?',
  answer: "Deal velocity in the lower-middle market is typically 6–12 months from first introduction to close. The pilot creates a pipeline — it doesn't guarantee a close within 90 days (and anyone promising that isn't being honest). What it does guarantee: 30–45 qualified introductions with real, screened business owners. The pipeline you build during the pilot continues producing results for months after it ends."
}];
export function Buyers({
  onBuyerContactOpen
}: BuyersProps) {
  const [openObjection, setOpenObjection] = useState<number | null>(null);
  return <main className="bg-deal-black pt-20" data-id="element-156">
      {/* Hero Section */}
      <section className="relative min-h-[80vh] flex items-center justify-center overflow-hidden" data-id="element-157">
        <div className="absolute inset-0 opacity-[0.025]" style={{
        backgroundImage: `radial-gradient(circle, rgba(255,255,255,0.8) 1px, transparent 1px)`,
        backgroundSize: '40px 40px'
      }} data-id="element-158" />
        <div className="absolute inset-0 bg-gradient-to-b from-deal-black/20 via-transparent to-deal-black" data-id="element-159" />

        <div className="relative z-10 max-w-5xl mx-auto px-6 text-center pt-20 pb-20" data-id="element-160">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-161">
            <span className="inline-block text-deal-green text-[11px] font-semibold tracking-[0.25em] uppercase mb-8 border border-deal-green/30 px-4 py-1.5" data-id="element-162">
              For PE Firms & Family Offices
            </span>
          </motion.div>

          <motion.h1 initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.7,
          delay: 0.1
        }} className="text-5xl md:text-6xl lg:text-[72px] font-bold text-white leading-[1.05] tracking-tight mb-8" data-id="element-163">
            Off-Market Seller Introductions
            <br data-id="element-164" />
            <span className="text-deal-green" data-id="element-165">in Your Buy Box</span>
          </motion.h1>

          <motion.p initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="text-lg md:text-xl text-white/50 max-w-3xl mx-auto mb-12 leading-relaxed font-light" data-id="element-166">
            Predictable monthly output: qualified owner conversations with
            screening-ready context. No auctions. No noise. Just actionable,
            proprietary deal flow.
          </motion.p>

          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.3
        }} className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-167">
            <button onClick={onBuyerContactOpen} className="px-8 py-4 bg-deal-green text-black text-sm font-bold tracking-widest uppercase hover:bg-deal-green-dark transition-colors duration-300 flex items-center gap-2" data-id="element-168">
              Start a 90-Day Pilot
              <ArrowRightIcon className="w-4 h-4" data-id="element-169" />
            </button>
            <Link to="/sample-deal-brief" className="px-8 py-4 border-2 border-dashed border-deal-green text-deal-green text-sm font-bold tracking-widest uppercase hover:bg-deal-green/10 transition-colors duration-300" data-id="element-170">
              Download Sample Deal Brief
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Scrolling Ticker */}
      <ScrollingTicker items={tickerItems} data-id="element-171" />

      {/* The Sourcing Problem Section */}
      <section className="py-28 md:py-36 bg-[#0d0d0d]" data-id="element-172">
        <div className="max-w-6xl mx-auto px-6 lg:px-10" data-id="element-173">
          <motion.div {...fadeInUp} className="mb-16" data-id="element-174">
            <p className="text-[11px] text-deal-green tracking-[0.25em] uppercase font-semibold mb-6" data-id="element-175">
              The Sourcing Problem
            </p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight max-w-3xl mb-6" data-id="element-176">
              Your current sourcing model has a math problem.
            </h2>
            <p className="text-white/50 text-lg leading-relaxed font-light max-w-2xl" data-id="element-177">
              Traditional proprietary sourcing yields roughly 1% close rates.
              That means your team reviews 80–100 opportunities to close a
              single deal. Most of that effort is wasted on leads that were
              never real to begin with.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-white/[0.06]" data-id="element-178">
            {/* Traditional Sourcing */}
            <motion.div {...fadeInUp} className="bg-[#0d0d0d] p-10 md:p-12" data-id="element-179">
              <p className="text-white/40 text-[11px] tracking-[0.2em] uppercase font-semibold mb-6" data-id="element-180">
                Traditional Sourcing
              </p>
              <div className="text-6xl md:text-7xl font-bold text-white/30 mb-2" data-id="element-181">
                ~1%
              </div>
              <p className="text-white/40 text-sm mb-6" data-id="element-182">
                ~80 opportunities reviewed → 1 close
              </p>
              <p className="text-white/50 leading-relaxed text-sm" data-id="element-183">
                Cold outreach, unqualified leads, recycled broker deals,
                inflated projections, misaligned sellers, wasted senior partner
                time. Your team spends 80% of their effort filtering noise — and
                20% on deals that actually matter.
              </p>
            </motion.div>

            {/* This Model */}
            <motion.div {...fadeInUp} transition={{
            delay: 0.1
          }} className="bg-[#0d0d0d] p-10 md:p-12" data-id="element-184">
              <p className="text-deal-green text-[11px] tracking-[0.2em] uppercase font-semibold mb-6" data-id="element-185">
                This Model
              </p>
              <div className="text-6xl md:text-7xl font-bold text-deal-green mb-2" data-id="element-186">
                ~12%
              </div>
              <p className="text-deal-green/70 text-sm mb-6" data-id="element-187">
                25 qualified introductions → ~3 actionable deals
              </p>
              <p className="text-white/50 leading-relaxed text-sm" data-id="element-188">
                Pre-screened, motivated owners who match your buy box.
                Structured deal briefs with financial context, motivation,
                risks, and clear next-step recommendations. Your team reviews
                fewer opportunities — but dramatically better ones.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* What You Receive Section */}
      <section className="py-28 md:py-36 bg-deal-black border-t border-white/[0.06]" data-id="element-189">
        <div className="max-w-6xl mx-auto px-6 lg:px-10" data-id="element-190">
          <motion.div {...fadeInUp} className="mb-16" data-id="element-191">
            <p className="text-[11px] text-deal-green tracking-[0.25em] uppercase font-semibold mb-6" data-id="element-192">
              What You Receive
            </p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight max-w-3xl mb-6" data-id="element-193">
              Delivered outputs your team can act on.
            </h2>
            <p className="text-white/50 text-lg leading-relaxed font-light max-w-2xl" data-id="element-194">
              Each qualified introduction comes with a structured deal brief
              designed for fast investment committee triage. No guesswork. No
              extra research required.
            </p>
          </motion.div>

          <motion.div variants={staggerContainer} initial="initial" whileInView="whileInView" viewport={{
          once: true
        }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/[0.06]" data-id="element-195">
            <motion.div variants={fadeInUp} className="bg-[#0d0d0d] p-10 md:p-12 group" data-id="element-196">
              <div className="w-10 h-10 bg-deal-green/10 rounded-lg flex items-center justify-center mb-6 group-hover:bg-deal-green/20 transition-colors" data-id="element-197">
                <ClipboardListIcon className="w-5 h-5 text-deal-green" data-id="element-198" />
              </div>
              <h3 className="text-lg font-bold text-white mb-3" data-id="element-199">
                Financial Overview
              </h3>
              <p className="text-white/50 text-sm leading-relaxed" data-id="element-200">
                Revenue and EBITDA ranges as provided and validated in direct
                conversation with the owner. Not estimates from a database —
                real numbers confirmed in a real call.
              </p>
            </motion.div>

            <motion.div variants={fadeInUp} className="bg-[#0d0d0d] p-10 md:p-12 group" data-id="element-201">
              <div className="w-10 h-10 bg-deal-green/10 rounded-lg flex items-center justify-center mb-6 group-hover:bg-deal-green/20 transition-colors" data-id="element-202">
                <TargetIcon className="w-5 h-5 text-deal-green" data-id="element-203" />
              </div>
              <h3 className="text-lg font-bold text-white mb-3" data-id="element-204">
                Motivation & Timing
              </h3>
              <p className="text-white/50 text-sm leading-relaxed" data-id="element-205">
                Why is this owner exploring? Succession planning, burnout,
                de-risking, growth capital? And when — actively looking (0–6
                months), planning ahead (6–12), or early exploration (12–24).
              </p>
            </motion.div>

            <motion.div variants={fadeInUp} className="bg-[#0d0d0d] p-10 md:p-12 group" data-id="element-206">
              <div className="w-10 h-10 bg-deal-green/10 rounded-lg flex items-center justify-center mb-6 group-hover:bg-deal-green/20 transition-colors" data-id="element-207">
                <UserIcon className="w-5 h-5 text-deal-green" data-id="element-208" />
              </div>
              <h3 className="text-lg font-bold text-white mb-3" data-id="element-209">
                Owner Involvement & Transition
              </h3>
              <p className="text-white/50 text-sm leading-relaxed" data-id="element-210">
                How involved is the owner in daily operations? Are they willing
                to stay through a transition period? What does the management
                bench look like? Critical context for post-close planning.
              </p>
            </motion.div>

            <motion.div variants={fadeInUp} className="bg-[#0d0d0d] p-10 md:p-12 group" data-id="element-211">
              <div className="w-10 h-10 bg-deal-green/10 rounded-lg flex items-center justify-center mb-6 group-hover:bg-deal-green/20 transition-colors" data-id="element-212">
                <BarChart3Icon className="w-5 h-5 text-deal-green" data-id="element-213" />
              </div>
              <h3 className="text-lg font-bold text-white mb-3" data-id="element-214">
                Concentration Snapshot
              </h3>
              <p className="text-white/50 text-sm leading-relaxed" data-id="element-215">
                Top customer as a percentage of revenue — the single fastest
                deal-killer in lower-middle market transactions. We surface it
                upfront so you don't waste diligence cycles discovering it
                later.
              </p>
            </motion.div>

            <motion.div variants={fadeInUp} className="bg-[#0d0d0d] p-10 md:p-12 group" data-id="element-216">
              <div className="w-10 h-10 bg-deal-green/10 rounded-lg flex items-center justify-center mb-6 group-hover:bg-deal-green/20 transition-colors" data-id="element-217">
                <AlertTriangleIcon className="w-5 h-5 text-deal-green" data-id="element-218" />
              </div>
              <h3 className="text-lg font-bold text-white mb-3" data-id="element-219">
                Key Risks & Flags
              </h3>
              <p className="text-white/50 text-sm leading-relaxed" data-id="element-220">
                We don't sugarcoat. Every deal brief includes our honest
                assessment of risks, concerns, and potential deal-breakers
                identified during screening. Better to know now than in month 3
                of diligence.
              </p>
            </motion.div>

            <motion.div variants={fadeInUp} className="bg-[#0d0d0d] p-10 md:p-12 group" data-id="element-221">
              <div className="w-10 h-10 bg-deal-green/10 rounded-lg flex items-center justify-center mb-6 group-hover:bg-deal-green/20 transition-colors" data-id="element-222">
                <PhoneIcon className="w-5 h-5 text-deal-green" data-id="element-223" />
              </div>
              <h3 className="text-lg font-bold text-white mb-3" data-id="element-224">
                Call Notes, Recording & Recommendation
              </h3>
              <p className="text-white/50 text-sm leading-relaxed" data-id="element-225">
                Full call recording and detailed notes from our screening
                conversation, plus our next-step recommendation graded A
                (advance immediately), B (qualify further), or C (park/monitor).
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* 90-Day Pilot Section */}
      <section className="py-28 md:py-36 bg-[#0d0d0d]" data-id="element-226">
        <div className="max-w-6xl mx-auto px-6 lg:px-10" data-id="element-227">
          <motion.div {...fadeInUp} className="mb-16" data-id="element-228">
            <p className="text-[11px] text-deal-green tracking-[0.25em] uppercase font-semibold mb-6" data-id="element-229">
              The 90-Day Pilot
            </p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight max-w-3xl mb-6" data-id="element-230">
              Defined outputs. Transparent pricing.
            </h2>
            <p className="text-white/50 text-lg leading-relaxed font-light max-w-2xl" data-id="element-231">
              No hidden fees. No variable costs. No long-term contracts. You
              know exactly what you're paying, what you're getting, and when
              you'll get it.
            </p>
          </motion.div>

          {/* Pricing Block */}
          <motion.div {...fadeInUp} className="mb-16" data-id="element-232">
            <div className="bg-[#111] border border-deal-green/30 p-10 md:p-12 max-w-xl" data-id="element-233">
              <p className="text-deal-green text-[11px] tracking-[0.2em] uppercase font-semibold mb-4" data-id="element-234">
                90-Day Pilot
              </p>
              <div className="text-5xl md:text-6xl font-bold text-white mb-2" data-id="element-235">
                $180,000
              </div>
              <p className="text-white/40 text-sm" data-id="element-236">
                $60,000/month · Billed in advance · No long-term contract
              </p>
            </div>
          </motion.div>

          {/* Monthly Milestones */}
          <motion.div {...fadeInUp} className="mb-16" data-id="element-237">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/[0.06]" data-id="element-238">
              <div className="bg-[#0d0d0d] p-8 text-center" data-id="element-239">
                <p className="text-white/40 text-xs uppercase tracking-widest mb-2" data-id="element-240">
                  Month 1
                </p>
                <p className="text-2xl font-bold text-white" data-id="element-241">8–12</p>
                <p className="text-white/50 text-sm" data-id="element-242">qualified introductions</p>
              </div>
              <div className="bg-[#0d0d0d] p-8 text-center" data-id="element-243">
                <p className="text-white/40 text-xs uppercase tracking-widest mb-2" data-id="element-244">
                  Month 2
                </p>
                <p className="text-2xl font-bold text-white" data-id="element-245">10–15</p>
                <p className="text-white/50 text-sm" data-id="element-246">qualified introductions</p>
              </div>
              <div className="bg-[#0d0d0d] p-8 text-center" data-id="element-247">
                <p className="text-white/40 text-xs uppercase tracking-widest mb-2" data-id="element-248">
                  Month 3
                </p>
                <p className="text-2xl font-bold text-white" data-id="element-249">12–18</p>
                <p className="text-white/50 text-sm" data-id="element-250">qualified introductions</p>
              </div>
            </div>
          </motion.div>

          {/* Expected Outcomes */}
          <motion.div {...fadeInUp} data-id="element-251">
            <p className="text-white font-bold text-lg mb-6" data-id="element-252">
              Expected Outcomes
            </p>
            <ul className="space-y-4" data-id="element-253">
              <li className="flex items-start gap-4" data-id="element-254">
                <div className="w-5 h-5 rounded-full bg-deal-green/10 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-255">
                  <CheckIcon className="w-3 h-3 text-deal-green" data-id="element-256" />
                </div>
                <span className="text-white/70" data-id="element-257">
                  30–45 total qualified introductions — off-market business
                  owners who match your buy box and are actively exploring
                </span>
              </li>
              <li className="flex items-start gap-4" data-id="element-258">
                <div className="w-5 h-5 rounded-full bg-deal-green/10 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-259">
                  <CheckIcon className="w-3 h-3 text-deal-green" data-id="element-260" />
                </div>
                <span className="text-white/70" data-id="element-261">
                  ~3–5 actionable deals from those introductions — opportunities
                  your team will want to advance
                </span>
              </li>
              <li className="flex items-start gap-4" data-id="element-262">
                <div className="w-5 h-5 rounded-full bg-deal-green/10 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-263">
                  <CheckIcon className="w-3 h-3 text-deal-green" data-id="element-264" />
                </div>
                <span className="text-white/70" data-id="element-265">
                  ~1–2 opportunities progressing to IOI/LOI stage within the
                  pilot window (timing dependent on deal velocity)
                </span>
              </li>
              <li className="flex items-start gap-4" data-id="element-266">
                <div className="w-5 h-5 rounded-full bg-deal-green/10 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-267">
                  <CheckIcon className="w-3 h-3 text-deal-green" data-id="element-268" />
                </div>
                <span className="text-white/70" data-id="element-269">
                  A growing proprietary pipeline — relationships started during
                  the pilot that may convert in months 4–12
                </span>
              </li>
            </ul>
          </motion.div>
        </div>
      </section>

      {/* Buy Box Alignment Section */}
      <section className="py-28 md:py-36 bg-deal-black border-t border-white/[0.06]" data-id="element-270">
        <div className="max-w-6xl mx-auto px-6 lg:px-10" data-id="element-271">
          <motion.div {...fadeInUp} className="mb-16" data-id="element-272">
            <p className="text-[11px] text-deal-green tracking-[0.25em] uppercase font-semibold mb-6" data-id="element-273">
              Buy Box Alignment
            </p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight max-w-3xl mb-6" data-id="element-274">
              We align on your criteria before we start.
            </h2>
            <p className="text-white/50 text-lg leading-relaxed font-light max-w-2xl" data-id="element-275">
              Every pilot begins with a detailed buy box calibration. We only
              deliver introductions that match your parameters.
            </p>
          </motion.div>

          {/* Parameters */}
          <motion.div {...fadeInUp} className="mb-16" data-id="element-276">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/[0.06]" data-id="element-277">
              <div className="bg-[#0d0d0d] p-6" data-id="element-278">
                <p className="text-white/40 text-xs uppercase tracking-widest mb-2" data-id="element-279">
                  EBITDA Range
                </p>
                <p className="text-white font-semibold" data-id="element-280">$5–10M</p>
              </div>
              <div className="bg-[#0d0d0d] p-6" data-id="element-281">
                <p className="text-white/40 text-xs uppercase tracking-widest mb-2" data-id="element-282">
                  Geography
                </p>
                <p className="text-white font-semibold" data-id="element-283">US-wide</p>
              </div>
              <div className="bg-[#0d0d0d] p-6" data-id="element-284">
                <p className="text-white/40 text-xs uppercase tracking-widest mb-2" data-id="element-285">
                  Exclusions
                </p>
                <p className="text-white font-semibold" data-id="element-286">Tech + Oil & Gas</p>
              </div>
              <div className="bg-[#0d0d0d] p-6" data-id="element-287">
                <p className="text-white/40 text-xs uppercase tracking-widest mb-2" data-id="element-288">
                  Deal Type
                </p>
                <p className="text-white font-semibold" data-id="element-289">
                  Control / Majority preferred
                </p>
              </div>
              <div className="bg-[#0d0d0d] p-6" data-id="element-290">
                <p className="text-white/40 text-xs uppercase tracking-widest mb-2" data-id="element-291">
                  Sector Focus
                </p>
                <p className="text-white font-semibold" data-id="element-292">
                  Industry-agnostic or 3–5 priority sectors
                </p>
              </div>
              <div className="bg-[#0d0d0d] p-6" data-id="element-293">
                <p className="text-white/40 text-xs uppercase tracking-widest mb-2" data-id="element-294">
                  Output Format
                </p>
                <p className="text-white font-semibold" data-id="element-295">
                  Structured deal briefs
                </p>
              </div>
            </div>
          </motion.div>

          {/* No Lock-In Callout */}
          <motion.div {...fadeInUp} className="mb-16" data-id="element-296">
            <div className="bg-[#111] border-l-2 border-deal-green p-8" data-id="element-297">
              <h3 className="text-xl font-bold text-white mb-3" data-id="element-298">
                No Lock-In. No Long-Term Contracts.
              </h3>
              <p className="text-white/50 leading-relaxed" data-id="element-299">
                The pilot is 90 days. Monthly billing. If the outputs don't
                justify the investment, you stop — no penalties, no awkward
                conversations, no sunk-cost obligations. We believe in earning
                your business every month with measurable results. That
                alignment is what keeps us accountable — and what gives you the
                confidence to start.
              </p>
            </div>
          </motion.div>

          {/* Capacity Note */}
          <motion.div {...fadeInUp} data-id="element-300">
            <p className="text-white/40 text-sm leading-relaxed mb-6 italic" data-id="element-301">
              To maintain output quality and avoid buyer overlap, we work with a
              limited number of buyers simultaneously per sector and geography.
              If your buy box overlaps with an existing engagement, we may not
              be able to onboard immediately. The fit call takes 10 minutes —
              and confirms whether we have open capacity for your criteria.
            </p>
            <button onClick={onBuyerContactOpen} className="px-8 py-4 bg-deal-green text-black text-sm font-bold tracking-widest uppercase hover:bg-deal-green-dark transition-colors duration-300 flex items-center gap-2" data-id="element-302">
              Check Availability
              <ArrowRightIcon className="w-4 h-4" data-id="element-303" />
            </button>
          </motion.div>
        </div>
      </section>

      {/* Common Concerns Section */}
      <section className="py-28 md:py-36 bg-[#0d0d0d]" data-id="element-304">
        <div className="max-w-4xl mx-auto px-6 lg:px-10" data-id="element-305">
          <motion.div {...fadeInUp} className="mb-12" data-id="element-306">
            <p className="text-[11px] text-deal-green tracking-[0.25em] uppercase font-semibold mb-6" data-id="element-307">
              Common Concerns
            </p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white" data-id="element-308">
              Objections we've heard (and answered).
            </h2>
          </motion.div>

          <div className="divide-y divide-white/[0.06]" data-id="element-309">
            {objections.map((item, i) => <div key={i} data-id="element-310">
                <button onClick={() => setOpenObjection(openObjection === i ? null : i)} className="w-full flex items-center justify-between text-left py-6 focus:outline-none group" data-id="element-311">
                  <span className="text-lg font-medium text-white/80 group-hover:text-white transition-colors pr-8" data-id="element-312">
                    "{item.question}"
                  </span>
                  <ChevronDownIcon className={`w-5 h-5 text-white/30 flex-shrink-0 transition-transform duration-300 ${openObjection === i ? 'rotate-180' : ''}`} data-id="element-313" />
                </button>
                <AnimatePresence data-id="element-314">
                  {openObjection === i && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.3
              }} className="overflow-hidden" data-id="element-315">
                      <p className="text-white/50 text-base leading-relaxed pb-8 pr-8 font-light" data-id="element-316">
                        {item.answer}
                      </p>
                    </motion.div>}
                </AnimatePresence>
              </div>)}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-28 md:py-36 bg-deal-black relative overflow-hidden border-t border-white/[0.06]" data-id="element-317">
        <div className="absolute inset-0 bg-gradient-to-t from-deal-green/[0.04] to-transparent" data-id="element-318" />

        <div className="relative z-10 max-w-4xl mx-auto px-6 lg:px-10 text-center" data-id="element-319">
          <motion.div {...fadeInUp} data-id="element-320">
            <p className="text-[11px] text-deal-green tracking-[0.25em] uppercase font-semibold mb-6" data-id="element-321">
              Get Started
            </p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight" data-id="element-322">
              Start your 90-day pilot.
            </h2>
            <p className="text-lg text-white/45 mb-10 max-w-2xl mx-auto font-light" data-id="element-323">
              Tell us about your buy box. We'll confirm fit and capacity in a
              10-minute call.
            </p>
            <button onClick={onBuyerContactOpen} className="px-8 py-4 bg-deal-green text-black text-sm font-bold tracking-widest uppercase hover:bg-deal-green-dark transition-colors duration-300 flex items-center gap-2 mx-auto" data-id="element-324">
              Start Your 90-Day Pilot
              <ArrowRightIcon className="w-4 h-4" data-id="element-325" />
            </button>
          </motion.div>
        </div>
      </section>
    </main>;
}